import { FC, PropsWithChildren } from 'react';
import { twMerge } from 'tailwind-merge';

type TFooterGrid = FC<PropsWithChildren<{}>> & {
  LinkCollectionSegment: FC<PropsWithChildren<{}>>;
  MetaSegment: FC<PropsWithChildren<{ className?: string }>>;
  ScrollToTopSegment: FC<PropsWithChildren<{}>>;
};

/**
 * Wraps given children in a footer grid.
 * Use FooterGrid.Segment, FooterGrid.MetaSegment or FooterGrid.ScrollToTopSegment as children.
 */
const FooterGrid: TFooterGrid = ({ children }) => {
  return (
    <ul className="m-0 flex list-none flex-col items-stretch justify-between p-0 lg:flex-row">
      {children}
    </ul>
  );
};

/**
 * Wraps given children in a footer grid link collection segment.
 * Use this segment to wrap the footer's links.
 */
const LinkCollectionSegment: TFooterGrid['LinkCollectionSegment'] = ({
  children,
}) => {
  return (
    <li className="w-full grow-0 lg:mr-[1.25rem] lg:max-w-[12.5rem]">
      {children}
    </li>
  );
};

/**
 * Wraps given children in a footer grid meta segment.
 * Use this segment to wrap the meta children.
 */
const MetaSegment: TFooterGrid['MetaSegment'] = ({ children, className }) => {
  return (
    <li className={twMerge('m-0 w-full max-w-none grow-0', className)}>
      {children}
    </li>
  );
};

/**
 * Wraps given children in a footer grid scroll to top segment.
 * Use this segment to wrap the scroll to top button.
 */
const ScrollToTopSegment: TFooterGrid['ScrollToTopSegment'] = ({
  children,
}) => {
  return (
    <li className="w-full grow-0 bg-secondary-marine-100 text-white lg:mr-[1.25rem] lg:hidden lg:max-w-[12.5rem]">
      {children}
    </li>
  );
};

FooterGrid.LinkCollectionSegment = LinkCollectionSegment;
FooterGrid.MetaSegment = MetaSegment;
FooterGrid.ScrollToTopSegment = ScrollToTopSegment;

export default FooterGrid;
